// utils/debounce.ts
import useIDEStore from "./useIDEStore";

/**
 * Creates a debounced function that delays invoking func until after wait milliseconds
 * @param func The function to debounce
 * @param wait The number of milliseconds to delay
 * @returns A debounced version of the function
 */
export const debounce = <F extends (...args: any[]) => any>(
    func: F,
    waitFor: number
): ((...args: Parameters<F>) => void) => {
    let timeout: ReturnType<typeof setTimeout> | null = null;

    return (...args: Parameters<F>): void => {
        if (timeout !== null) {
            clearTimeout(timeout);
        }

        timeout = setTimeout(() => func(...args), waitFor);
    };
};

export const createDebouncedProjectUpdate = (
    updateFn: (() => Promise<void>) | null,
    delay: number = 500
): ((structuralChange?: boolean, contentOnly?: boolean) => void) => {
    if (!updateFn) {
        return () => {}; // Return a no-op function if updateFn is null
    }

    return debounce(async (structuralChange: boolean = false, contentOnly: boolean = false) => {
        try {
            // Only call updateFn if it's not a content-only change
            if (!contentOnly) {
                await updateFn();
            }

            // Add logic for structural changes - FIXED: Use refreshFiles instead
            if (structuralChange) {
                await useIDEStore.getState().refreshFiles();
            }
        } catch (error) {
            console.error('[Debounced Project Update] Error:', error);
        }
    }, delay);
};

// Add this function to debounce.ts
/**
 * Create a version of onProjectUpdate that updates the store without resetting state
 * @param updateFn The original project update function
 * @param store The IDE store
 */
export const createSafeProjectUpdate = (
    updateFn: (() => Promise<void>) | null,
    store: any
): (() => Promise<void>) => {
    if (!updateFn) {
        return async () => {}; // Return a no-op function if updateFn is null
    }

    return async () => {
        try {
            // Call the original update function to get the new project state
            await updateFn();

            // Get the current state from the store
            const state = store.getState();

            // Get the updated project (this would now have the latest data)
            const userActor = state.userActor;
            const userPrincipal = state.userPrincipal;

            if (!userActor || !userPrincipal || !state.project?.id) {
                console.log('[createSafeProjectUpdate] Cannot perform safe project update - missing dependencies');
                return;
            }

            try {
                // Get the updated project data directly without using setProject
                const projects = await userActor.getUserProjects();
                const updatedProject = projects.find(p => p.id === state.project.id);

                if (updatedProject) {
                    // Update only the project property without resetting other state
                    store.setState({
                        project: updatedProject
                    });

                    console.log('[createSafeProjectUpdate] Safe project update completed');
                }
            } catch (error) {
                console.error('[createSafeProjectUpdate] Error in safe project update:', error);
            }
        } catch (error) {
            console.error('[createSafeProjectUpdate] Error:', error);
        }
    };
};